/*
 * @Author: Tahiti
 * @Date: 2025-12-18
 * @Description: 特效模板相关API接口
 * @FilePath: /weryai-web/api/effects.ts
 */
import { http } from '~/utils/request'

// 标签项类型
export interface EffectTemplateTag {
    id: number
    tag?: string
    name: string
    status?: string
}

// 标签列表响应类型
export interface EffectTemplateTagListResponse {
    status: number
    data: EffectTemplateTag[]
    message?: string
}

// 模板项类型
export interface EffectTemplateItem {
    id: number
    name: string
    cover_url: string
    path_url: string
    type: 'IMAGE' | 'VIDEO'
    tags: string[]
    video_urls: string[]
}

// 模板分页响应类型
export interface EffectTemplatePageResponse {
    status: number
    data: {
        content: EffectTemplateItem[]
        page_size: number
        total: number
        total_page: number
        extra?: Record<string, any>
    }
    message?: string
}

/**
 * @description 获取特效模板标签列表
 * @param lang 语言代码
 * @param type 模板类型 IMAGE | VIDEO
 */
export const getEffectTemplateTagListApi = (lang: string, type?: 'IMAGE' | 'VIDEO'): Promise<EffectTemplateTagListResponse> => {
    let url = `/api/v1/auth/effect/template/tag/list?lang=${lang}`
    if (type) {
        url += `&type=${type}`
    }
    return http.post(url)
}

/**
 * @description 分页查询特效模板列表
 * @param params 查询参数
 */
export const getEffectTemplatePageApi = (params: {
    name?: string
    type?: 'IMAGE' | 'VIDEO'
    status?: string
    tagId?: number
    top?: boolean
    pageNum?: number
    pageSize?: number
    lang: string
}): Promise<EffectTemplatePageResponse> => {
    const queryParams = new URLSearchParams()
    Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
            queryParams.append(key, String(value))
        }
    })
    return http.get(`/api/v1/auth/effect/template/page?${queryParams.toString()}`)
}

// 置顶模板列表响应类型
export interface EffectTemplateTopResponse {
    status: number
    data: EffectTemplateItem[]
    message?: string
    desc?: string
    success?: boolean
}

/**
 * @description 获取置顶模板列表
 * @param params 查询参数
 */
export const getEffectTemplateTopApi = (params: {
    type?: 'IMAGE' | 'VIDEO'
    lang: string
}): Promise<EffectTemplateTopResponse> => {
    const queryParams = new URLSearchParams()
    Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
            queryParams.append(key, String(value))
        }
    })
    return http.get(`/api/v1/auth/effect/template/top?${queryParams.toString()}`)
}

// 模板配置项类型
export interface EffectPromptVariable {
    key: string
    index: number
    label?: string
}

export interface EffectTemplateConfig {
    id: number
    template_id: number
    name: string
    cover_url: string
    aspect_ratio: string[]
    image_input: number
    sample_urls: string[]
    video_urls: string[]
    bgm: boolean
    duration: number
    credit: number
    recommended_points: number
    resolution: string
    prompt_customizable?: boolean
    prompt_display?: string
    prompt_variables?: EffectPromptVariable[]
}

// 模板详情类型
export interface EffectTemplateDetail {
    id: number
    name: string
    cover_url: string
    path_url: string
    type: 'IMAGE' | 'VIDEO'
    tags: string[]
    video_url: string
    configs: EffectTemplateConfig[]
    json_url?: string  // 模板介绍页 JSON 数据 URL
}

// 模板介绍页 JSON 数据类型
export interface EffectTemplateIntroJson {
    introduce: {
        title: string
        desc: string
        urls?: string[]
        medias?: {
            url: string
            thumbnail_url?: string
        }[]
    }
    select_reason: {
        title: string
        btn_info: string
        desc: {
            title: string
            desc: string
            url: string
            thumbnail_url?: string
        }[]
    }
    how_to_use: {
        title: string
        desc: string
        img: string
        steps: {
            title: string
            desc: string
        }[]
    }
    explore_others: {
        title: string
        show_types: {
            desc: string
            path: string
            icon?: string
            bg?: string
        }[]
    }
    question_answer: {
        title: string
        qa_data: {
            question: string
            answer: string
        }[]
    }
    start_use: {
        title: string
        btn_info: string
    }
}

// 模板详情响应类型
export interface EffectTemplateDetailResponse {
    status: number
    data: EffectTemplateDetail
    message?: string
    desc?: string
    success: boolean
}

/**
 * @description 根据路径获取特效模板详情
 * @param path 模板路径标识（如：spring、xmas-wrap-break）
 * @param lang 语言代码
 */
export const getEffectTemplateInfoApi = (path: string, lang: string) => {
    return http.get(`/api/v1/auth/effect/template/info?path=${encodeURIComponent(path)}&lang=${lang}`) as Promise<EffectTemplateDetailResponse>
}

// 特效模板创作请求参数
export interface EffectTemplateCreationReq {
    template_config_id: number
    image_url?: string[]
    prompt_inputs?: Record<string, string>
    aspect_ratio?: string
    bgm?: boolean
    copyright_protection?: boolean
    public_visible?: boolean
    platform?: string
    product_id?: number
    team_id?: string
}

// 特效模板创作响应
export interface EffectTemplateCreationResp {
    status: number
    data: {
        log_id: number
        result: string
        task_id: number
        third_task_id: string
    }
    message?: string
    success?: boolean
}

/**
 * @description 特效模板创作
 */
export const createEffectTemplateApi = (params: EffectTemplateCreationReq) => {
    return http.post('/api/v1/auth/creative/effect/template/creation', params as unknown as Record<string, unknown>) as Promise<EffectTemplateCreationResp>
}

// 特效模板最后更新时间响应类型
export interface EffectTemplateLastUpdateResponse {
    status: number
    data: number  // 时间戳 (long)
    message?: string
    desc?: string
    success?: boolean
}

/**
 * @description 查询特效模板最后更新时间
 * @param type 模板类型 IMAGE | VIDEO
 */
export const getEffectTemplateLastUpdateApi = (type: 'IMAGE' | 'VIDEO'): Promise<EffectTemplateLastUpdateResponse> => {
    return http.get(`/api/v1/auth/effect/template/last/update?type=${type}`)
}

